/**
 * Bookkeeping for per-group team hydration: each competition is attempted once per scope
 * (date / sport / filter / locale). Without this, a group whose response cannot satisfy
 * "every competitor has an entity" — a 502, an id the Search API omits, a match that changed
 * status between the two cache windows — would be fetched again on every render, forever.
 * After one attempt the group is "settled": rows render with whatever arrived.
 *
 * Pure; the hook wraps it and bumps a React state version when it changes.
 */
export class HydrationTracker {
    private inflight = new Set<string>();
    private attempted = new Set<string>();

    /** Begin an attempt. False when the id is in flight or was already attempted in this scope. */
    start(id: string): boolean {
        if (this.inflight.has(id) || this.attempted.has(id)) return false;
        this.inflight.add(id);
        this.attempted.add(id);
        return true;
    }

    finish(id: string): void {
        this.inflight.delete(id);
    }

    isLoading(id: string): boolean {
        return this.inflight.has(id);
    }

    /** Attempted and no longer in flight — whatever the outcome. */
    isSettled(id: string): boolean {
        return this.attempted.has(id) && !this.inflight.has(id);
    }

    reset(): void {
        this.inflight.clear();
        this.attempted.clear();
    }
}
